import { HealthActivityLevel } from './healthCalorieGoalCalc';
import { waterHpForEntry } from './healthWater';

export const WALKING_LOG_HP_REWARD = 1;

export const TRAINING_HP_MINUTES_PER_HP = 2;
export const CYCLING_HP_KM_PER_HP = 2;

export const MIN_TRAINING_KCAL = 1;
export const MAX_TRAINING_KCAL = 3000;

export const MIN_CYCLING_KM = 0.1;
export const MAX_CYCLING_KM = 300;

export const MIN_WALKING_STEPS = 1;
export const MAX_WALKING_STEPS = 100_000;
export const WALKING_KCAL_PER_STEP = 0.04;
export const WALKING_STRIDE_HEIGHT_RATIO = 0.415;

export const MIN_ACTIVITY_MINUTES = 1;
export const MAX_ACTIVITY_MINUTES = 600;

export const TRAINING_MET = 6;
export const CYCLING_MET = 8;
export const RECOVERY_BASE_MET = 1.5;
export const RECOVERY_DECAY_PER_MINUTE = 1;

export const DEFAULT_ESTIMATE_BASAL_KCAL = 1600;

export const DEFAULT_DAILY_STEP_GOAL = 9000;
export const MIN_DAILY_STEP_GOAL = 1000;
export const MAX_DAILY_STEP_GOAL = 50_000;
export const DAILY_STEP_GOAL_STEP = 500;

export const DAILY_STEP_GOAL_VALUES: number[] = Array.from(
    { length: Math.round((MAX_DAILY_STEP_GOAL - MIN_DAILY_STEP_GOAL) / DAILY_STEP_GOAL_STEP) + 1 },
    (_, i) => MIN_DAILY_STEP_GOAL + i * DAILY_STEP_GOAL_STEP,
);

export type ActivityAmountValidation = { ok: true; value: number } | { ok: false; message: string };

function validateAmountInRange(n: number, min: number, max: number, label: string, unit: string): ActivityAmountValidation {
    if (!Number.isFinite(n)) {
        return { ok: false, message: `Enter a valid ${label}` };
    }
    if (n < min || n > max) {
        return { ok: false, message: `${label[0].toUpperCase()}${label.slice(1)} must be between ${min} and ${max} ${unit}` };
    }
    return { ok: true, value: n };
}

export function validateTrainingKcal(n: number): ActivityAmountValidation {
    return validateAmountInRange(Math.round(n), MIN_TRAINING_KCAL, MAX_TRAINING_KCAL, 'calorie amount', 'kcal');
}

export function validateCyclingKm(n: number): ActivityAmountValidation {
    return validateAmountInRange(+n.toFixed(2), MIN_CYCLING_KM, MAX_CYCLING_KM, 'distance', 'km');
}

export function validateWalkingSteps(n: number): ActivityAmountValidation {
    return validateAmountInRange(Math.round(n), MIN_WALKING_STEPS, MAX_WALKING_STEPS, 'step count', 'steps');
}

export function validateActivityMinutes(n: number): ActivityAmountValidation {
    return validateAmountInRange(Math.round(n), MIN_ACTIVITY_MINUTES, MAX_ACTIVITY_MINUTES, 'duration', 'min');
}

export function estimateActivityKcalFromMinutes(minutes: number, basalKcal: number, met: number): number {
    const hourlyRestingKcal = (basalKcal > 0 ? basalKcal : DEFAULT_ESTIMATE_BASAL_KCAL) / 24;
    return Math.max(1, Math.round(hourlyRestingKcal * met * (minutes / 60)));
}

export function estimateTrainingKcal(minutes: number, basalKcal: number): number {
    return estimateActivityKcalFromMinutes(minutes, basalKcal, TRAINING_MET);
}

export function estimateCyclingKcal(minutes: number, basalKcal: number): number {
    return estimateActivityKcalFromMinutes(minutes, basalKcal, CYCLING_MET);
}

export function estimateActiveExerciseKcal(minutes: number, basalKcal: number, met: number, adjustment = 1): number {
    return estimateActivityKcalFromMinutes(minutes, basalKcal, Math.max(0, met - 1) * adjustment);
}

export function estimateActiveRecoveryKcal(minutes: number, basalKcal: number, workMet: number): number {
    if (minutes <= 0) return 0;
    const recoveryStartMet = Math.min(4.5, Math.max(RECOVERY_BASE_MET, RECOVERY_BASE_MET + (workMet - RECOVERY_BASE_MET) / 2));
    const recoveryMet =
        RECOVERY_BASE_MET +
        ((recoveryStartMet - RECOVERY_BASE_MET) * (1 - Math.exp(-RECOVERY_DECAY_PER_MINUTE * minutes))) /
            (RECOVERY_DECAY_PER_MINUTE * minutes);
    const restingKcalPerMinute = (basalKcal > 0 ? basalKcal : DEFAULT_ESTIMATE_BASAL_KCAL) / 1440;
    return Math.max(0, Math.round(restingKcalPerMinute * Math.max(0, recoveryMet - 1) * minutes));
}

export function walkingKcalFromSteps(steps: number, heightCm?: number | null, weightKg?: number | null): number {
    if (heightCm && weightKg) {
        const strideMeters = (heightCm / 100) * WALKING_STRIDE_HEIGHT_RATIO;
        return Math.round((steps * strideMeters * weightKg) / 2000);
    }
    return Math.round(steps * WALKING_KCAL_PER_STEP);
}

export function walkingHpForSteps(steps: number): number {
    return Math.max(1, Math.round((steps / 1000) * WALKING_LOG_HP_REWARD));
}

export function walkingHpEarnedForTotal(totalSteps: number): number {
    return Math.round((totalSteps / 1000) * WALKING_LOG_HP_REWARD);
}

export function walkingHpForEntry(entrySteps: number, priorTotalSteps: number, stepGoal: number | null): number {
    if (stepGoal == null) {
        return walkingHpForSteps(entrySteps);
    }
    const newTotalSteps = priorTotalSteps + entrySteps;
    const hpBefore = priorTotalSteps >= stepGoal ? walkingHpEarnedForTotal(priorTotalSteps) : 0;
    const hpAfter = newTotalSteps >= stepGoal ? walkingHpEarnedForTotal(newTotalSteps) : 0;
    return Math.max(0, hpAfter - hpBefore);
}

export function trainingHpForMinutes(minutes: number): number {
    return Math.max(1, Math.round(minutes / TRAINING_HP_MINUTES_PER_HP));
}

export function cyclingHpForKm(km: number): number {
    return Math.max(1, Math.round(km / CYCLING_HP_KM_PER_HP));
}

export type HealthActivityRewardEntry = {
    kind: 'TRAINING' | 'CYCLING' | 'WALKING' | 'WATER';
    distanceKm?: number | null;
    minutes?: number | null;
    ml?: number | null;
    steps?: number | null;
    timestamp: string | number | Date;
};

export function activityHpForEntries(
    entries: readonly HealthActivityRewardEntry[],
    stepGoal: number | null,
    waterGoalMl: number | null,
): number {
    let priorTotalSteps = 0;
    let priorTotalMl = 0;

    return [...entries]
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
        .reduce((total, entry) => {
            if (entry.kind === 'TRAINING') return total + trainingHpForMinutes(entry.minutes ?? 0);
            if (entry.kind === 'CYCLING') return total + cyclingHpForKm(entry.distanceKm ?? 0);
            if (entry.kind === 'WALKING') {
                const hp = walkingHpForEntry(entry.steps ?? 0, priorTotalSteps, stepGoal);
                priorTotalSteps += entry.steps ?? 0;
                return total + hp;
            }
            const hp = waterHpForEntry(entry.ml ?? 0, priorTotalMl, waterGoalMl);
            priorTotalMl += entry.ml ?? 0;
            return total + hp;
        }, 0);
}

export const TEF_PROTEIN_RATE = 0.25;
export const TEF_CARB_RATE = 0.075;
export const TEF_FAT_RATE = 0.025;
export const TEF_FLAT_RATE = 0.1;
export const TEF_MACRO_MISMATCH_TOLERANCE = 0.5;

const KCAL_PER_G_PROTEIN = 4;
const KCAL_PER_G_CARB = 4;
const KCAL_PER_G_FAT = 9;

export function computeTefKcal(proteinG: number, carbsG: number, fatG: number, totalKcalConsumed: number): number {
    if (totalKcalConsumed <= 0) return 0;
    const proteinKcal = proteinG * KCAL_PER_G_PROTEIN;
    const carbKcal = carbsG * KCAL_PER_G_CARB;
    const fatKcal = fatG * KCAL_PER_G_FAT;
    const macroKcal = proteinKcal + carbKcal + fatKcal;
    const mismatch = macroKcal <= 0 ? 1 : Math.abs(macroKcal - totalKcalConsumed) / totalKcalConsumed;
    if (mismatch > TEF_MACRO_MISMATCH_TOLERANCE) {
        return Math.round(totalKcalConsumed * TEF_FLAT_RATE);
    }
    return Math.round(proteinKcal * TEF_PROTEIN_RATE + carbKcal * TEF_CARB_RATE + fatKcal * TEF_FAT_RATE);
}

export const NEAT_BASELINE_KCAL: Record<HealthActivityLevel, number> = {
    [HealthActivityLevel.SEDENTARY]: 100,
    [HealthActivityLevel.LIGHT]: 200,
    [HealthActivityLevel.MODERATE]: 350,
    [HealthActivityLevel.ACTIVE]: 550,
    [HealthActivityLevel.ATHLETE]: 800,
};

export function neatBaselineKcalForActivityLevel(level: HealthActivityLevel): number {
    return NEAT_BASELINE_KCAL[level];
}

export type DailyStepGoalValidation = { ok: true; value: number | null } | { ok: false; message: string };

export function validateDailyStepGoal(n: number | null | undefined): DailyStepGoalValidation {
    if (n == null || !Number.isFinite(n) || n <= 0) {
        return { ok: true, value: null };
    }
    const rounded = Math.round(n / DAILY_STEP_GOAL_STEP) * DAILY_STEP_GOAL_STEP;
    if (rounded < MIN_DAILY_STEP_GOAL || rounded > MAX_DAILY_STEP_GOAL) {
        return { ok: false, message: `Step goal must be between ${MIN_DAILY_STEP_GOAL} and ${MAX_DAILY_STEP_GOAL} steps` };
    }
    return { ok: true, value: rounded };
}

export function normalizeDailyStepGoal(n: number | null | undefined): number | null {
    const checked = validateDailyStepGoal(n);
    if (!checked.ok) throw new Error('INVALID_DAILY_STEP_GOAL');
    return checked.value;
}
